import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H1, H3, Text } from '@amzn/stencil-react-components/text';
import { Badge, BadgeType } from '@amzn/stencil-react-components/badge';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { IconPlusSmall } from '@amzn/stencil-react-icons';

import { useMessages } from '../../shared/i18n/useMessages';
import { useCurrentUser } from '../../shared/auth';
import { useReviewWizard } from '../../features/audit-whiz/hooks/useReviewWizard';
import { useReviewPublish } from './useReviewPublish';
import { AstraQuestionsPanel } from '../../features/audit-whiz/components/AstraQuestionsPanel';
import { ReviewWizardStepper } from '../../features/audit-whiz/components/ReviewWizardStepper';
import { Divider } from '../../shared/ui/Divider';
import {
  EMPTY_CRITERIA_ATTACHMENTS,
  type CriteriaField,
  type CriteriaAttachments,
} from '../../features/audit-whiz/components/AuditCriteriaStep';
import { ActiveStepEditor } from '../../features/audit-whiz/components/ActiveStepEditor';
import {
  ReviewQuestionStatus,
  ReviewWizardStep,
  type GenerateToReviewNavState,
} from '../../features/audit-whiz/logic/reviewTypes';
import { PublishBlockedBanner } from '../../features/audit-whiz/components/PublishBlockedBanner';
import { useMandatoryTagGroups } from '../../features/audit-whiz/hooks/useMandatoryTagGroups';
import { ActionButton, buildFooterActions } from './ReviewWizardFooter';
import {
  criteriaFieldErrors,
  questionConfigFieldErrors,
} from '../../shared/domain/questionConfigValidation';

const PAGE_BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type PageKey =
  | 'pageTitle'
  | 'comingSoon'
  | 'criteriaStepHeading'
  | 'subStepHeading'
  | 'configStepHeading'
  | 'backButton'
  | 'nextButton'
  | 'skipForNowButton'
  | 'publishButton'
  | 'pageSubtitle'
  | 'metaSummary'
  | 'newPromptButton'
  | 'questionPositionLabel'
  | 'draftPill'
  | 'stepValidationErrors'
  | 'publishErrorBanner';

const TESTID = 'review-questions';

/** Generate screen — the "New Prompt & Source" button returns here. */
const GENERATE_ROUTE = '/question/generate';

/** Reference-stable empty blocks so the field-error selectors receive a stable
 *  argument when the wizard's block is null (no violations). */
const NO_CONFIG_BLOCK: readonly [] = [];
const NO_CRITERIA_BLOCK: readonly [] = [];

/**
 * "Review & publish Astra questions" screen (AuditWhiz WHIZ-002).
 *
 * Two-column review layout: the 3-step wizard (stepper + active-step editor +
 * action bar) on the left, the Astra-questions side panel on the right. Fully
 * driven by `useReviewWizard`, which owns the draft list, selection, and step
 * cursor and self-seeds from mock data (Phase 1 — no agent yet).
 *
 * Rendered on desktop through `V2Adapter` (see AppRouter.tsx), which supplies
 * the Stencil, i18n, and auth providers this page needs.
 */
export function ReviewQuestionsPage(): JSX.Element {
  const { t, f } = useMessages<PageKey>(PAGE_BUNDLE);
  const navigate = useNavigate();
  const location = useLocation();
  // Author's program, used to deep-link each published question to its detail
  // page from the side panel.
  const { programId } = useCurrentUser();

  // Questions Astra generated, handed over to seed the wizard — the ONLY seed
  // source (there is no mock fallback in the runtime path). `undefined` on
  // direct navigation and empty for a zero-question generation both render the
  // empty-review state. Each generated question already carries its own
  // source-SOP doc link from the agent, so no SOP-prefill is threaded here. The
  // single consumer-side seam of the generate→review handoff; the nav-state
  // array is itself reference-stable.
  const navState = location.state as GenerateToReviewNavState | null;
  const seedQuestions = navState?.generatedQuestions;

  // Mandatory tag groups feed the config validator's all-mandatory-tags rule
  // (sourced from the tag catalog, BU+program scoped). The wizard owns the
  // combined publish gate; this just supplies that one input.
  const mandatoryTagGroups = useMandatoryTagGroups();
  const wizard = useReviewWizard(mandatoryTagGroups, seedQuestions);

  const { questions } = wizard;
  const total = questions.length;
  const pendingCount = questions.filter(
    (q) => q.status === ReviewQuestionStatus.NEEDS_REVIEW,
  ).length;
  const publishedCount = total - pendingCount;

  const handleNewPrompt = (): void => {
    void navigate(GENERATE_ROUTE);
  };

  const { selectedQuestion } = wizard;

  // Per-question criteria attachments, owned here so they survive wizard step
  // navigation (AuditCriteriaStep unmounts when the user moves to another
  // step). Keyed by question id, so switching to a different draft naturally
  // shows that draft's files (and an unseen draft falls back to the empty map).
  // PHASE 1: button-only — these are UI-only File objects, not uploaded or
  // persisted onto the draft yet (upload → mediaId wiring is deferred).
  // A Map (not a keyed object) so the question-id lookups are method calls and
  // don't trip security/detect-object-injection.
  const [attachmentsByQuestion, setAttachmentsByQuestion] = useState<
    ReadonlyMap<string, CriteriaAttachments>
  >(() => new Map());

  // Key writes off the RENDERED question's id (`selectedQuestion?.id`), NOT
  // `wizard.selectedId`. renderActiveStep reads with `question.id`, and the
  // hook resolves selectedQuestion with a `?? questions.at(0)` fallback when
  // selectedId drifts off the list — so keying the write off selectedId would
  // store files under a stale id the read never looks up. Same id on both
  // sides keeps the rendered question's files displayable.
  const activeQuestionId = selectedQuestion?.id;
  const handleAttachmentsChange = useCallback(
    (field: CriteriaField, files: File[]) => {
      if (activeQuestionId === undefined) return;
      setAttachmentsByQuestion((prev) => {
        const current = prev.get(activeQuestionId) ?? EMPTY_CRITERIA_ATTACHMENTS;
        const next = new Map(prev);
        next.set(activeQuestionId, { ...current, [field]: files });
        return next;
      });
    },
    [activeQuestionId],
  );

  // Published drafts are read-only on the review screen.
  const isPublished = selectedQuestion?.status === ReviewQuestionStatus.PUBLISHED;

  // Save & publish orchestration (create-question mutation + media uploads +
  // identity) lives in a page-colocated hook; the wizard owns only validation.
  const { runPublish, isPublishing, publishError, clearPublishError } =
    useReviewPublish();

  const handlePublish = useCallback((): void => {
    // Validate + resolve the target in the wizard (single gate shared with the
    // banner). `null` → blocked/invalid, so this is a no-op.
    const target = wizard.beginPublish();
    if (!target) return;
    const attachments =
      attachmentsByQuestion.get(target.id) ?? EMPTY_CRITERIA_ATTACHMENTS;
    runPublish(target, attachments, wizard.markPublished);
  }, [wizard, attachmentsByQuestion, runPublish]);

  // 1-based position of the draft ACTUALLY rendered — keyed off
  // `selectedQuestion?.id` (which has a `?? questions.at(0)` fallback), not
  // `selectedId`, so selection drift after a delete can't show "Question 0 of
  // N" while question 1 is on screen. 0 only when the list is empty.
  const selectedPosition = questions.findIndex((q) => q.id === selectedQuestion?.id) + 1;

  // Status pill beside the wizard header: the published Question ID once saved,
  // otherwise the "draft — no ID yet" copy.
  const publishedQuestionId = selectedQuestion?.questionId;
  const statusPillType = isPublished ? BadgeType.Positive : BadgeType.Neutral;
  const statusPillValue =
    isPublished && publishedQuestionId ? publishedQuestionId : t('draftPill');

  // Stepper labels keyed by the wizard-step enum (Map, not object, so the
  // union-typed lookup doesn't trip security/detect-object-injection).
  //
  // NOT memoized: `t` is referentially stable across renders (see useMessages),
  // so a `useMemo(..., [t])` would compute exactly once — on the first render,
  // before the i18n bundle resolves, when `t(key)` still returns the raw key —
  // and then never recompute, freezing the raw keys into the labels. Building
  // the 3-entry Map inline each render re-calls `t` after the bundle resolves.
  const stepLabels: ReadonlyMap<ReviewWizardStep, string> = new Map([
    [ReviewWizardStep.AUDIT_CRITERIA, t('criteriaStepHeading')],
    [ReviewWizardStep.SUB_QUESTIONS, t('subStepHeading')],
    [ReviewWizardStep.QUESTION_CONFIGURATION, t('configStepHeading')],
  ]);

  const { skipAction, stepActions } = buildFooterActions(
    wizard,
    t,
    Boolean(isPublished),
    TESTID,
    handlePublish,
    isPublishing,
  );

  return (
    <Col
      gridGap="dimensions.spacing.400"
      width="100%"
      padding={{ top: 'dimensions.spacing.300', bottom: 'dimensions.spacing.600' }}
      data-testid={`${TESTID}-page`}
    >
      {/* Full-width page header — title + count and the New Prompt & Source
          action on one row (title left, action far top-right), then the
          description and meta summary. Spans the whole page above the columns,
          matching the mock. */}
      <Col gridGap="dimensions.spacing.100">
        <Row justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
          <Row gridGap="dimensions.spacing.200" alignItems="center">
            <H1>{t('pageTitle')}</H1>
            <Badge type={BadgeType.Neutral} value={String(total)} />
          </Row>
          <Button
            variant={ButtonVariant.Secondary}
            icon={<IconPlusSmall aria-hidden="true" />}
            onClick={handleNewPrompt}
            data-testid={`${TESTID}-new-prompt`}
          >
            {t('newPromptButton')}
          </Button>
        </Row>
        <Text color="color.text.secondary">{t('pageSubtitle')}</Text>
        <Text fontSize="T100" color="color.text.secondary">
          {f('metaSummary', { pending: pendingCount, published: publishedCount })}
        </Text>
      </Col>

      {/* Two columns below the header: the wizard (2/3) and the Astra panel
          (1/3). */}
      <Row
        gridGap="dimensions.spacing.400"
        width="100%"
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <Col flex={2} minWidth={0} gridGap="dimensions.spacing.400">
          {/* One outer card wraps the wizard header, stepper, and active step —
              matching the mock, where all three read as a single bordered
              panel. The step components render card-less content (Audit
              criteria flat; Sub Questions / Configuration keep only their inner
              section cards), so there's no card-in-card. */}
          <Card padding="dimensions.spacing.400">
            <Col gridGap="dimensions.spacing.400" width="100%">
              {/* Publish-blocked banner: names the sub-questions that must be
                  fixed before Save & publish can proceed. Pinned to the TOP of
                  the wizard card so it's the first thing the author sees.
                  Rendered from the wizard's LIVE publishBlock — it appears the
                  instant a select sub drops below two options and clears the
                  instant it's fixed (mirrors perform-audit's live guidance), so
                  it's non-dismissible: a dismiss would just reappear on the
                  next keystroke. */}
              {/* Cross-step banner: ONLY on the final (Configuration) step, and
                  only the step-1 (criteria) + step-2 (sub-question) issues —
                  earlier steps use the stepper marker, and config errors show
                  inline (not here). */}
              {wizard.currentStep === ReviewWizardStep.QUESTION_CONFIGURATION ? (
                <PublishBlockedBanner
                  violations={wizard.publishBlock}
                  criteriaViolations={wizard.criteriaBlock}
                  dataTestId={`${TESTID}-publish-blocked`}
                />
              ) : null}

              {/* Publish API error (upload/create failure) — distinct from the
                  validation banner above; dismissible, auto-clears on retry. */}
              {publishError ? (
                <MessageBanner
                  type={MessageBannerType.Error}
                  isDismissible
                  onDismissed={clearPublishError}
                  data-testid={`${TESTID}-publish-error`}
                >
                  {f('publishErrorBanner', { message: publishError })}
                </MessageBanner>
              ) : null}

              {/* Wizard header — position + draft/published status pill. */}
              {selectedQuestion && (
                <Row gridGap="dimensions.spacing.200" alignItems="center">
                  {/* H3 (smaller than the step heading's H2) so the step
                      heading reads as slightly larger, matching the mock. */}
                  <H3>
                    {f('questionPositionLabel', { current: selectedPosition, total })}
                  </H3>
                  <Badge type={statusPillType} value={statusPillValue} />
                </Row>
              )}

              {/* Inner gray band for the stepper (matches the mock's stepper
                  strip inside the card). Muted surface fill — NOT the tracker's
                  `darkBackground` colorScheme, which is for dark surfaces and
                  would turn the labels light/unreadable on this light band. */}
              <Card
                padding="dimensions.spacing.300"
                backgroundColor="color.surface.bg-muted"
              >
                <ReviewWizardStepper
                  currentStep={wizard.currentStep}
                  onStepSelect={wizard.goToStep}
                  labels={stepLabels}
                  stepHasErrors={wizard.stepHasErrors}
                  warningText={t('stepValidationErrors')}
                />
              </Card>

              <ActiveStepEditor
                step={wizard.currentStep}
                question={selectedQuestion}
                onQuestionChange={wizard.onQuestionChange}
                disabled={Boolean(isPublished)}
                attachments={
                  (selectedQuestion && attachmentsByQuestion.get(selectedQuestion.id)) ||
                  EMPTY_CRITERIA_ATTACHMENTS
                }
                onAttachmentsChange={handleAttachmentsChange}
                criteriaFieldErrors={criteriaFieldErrors(
                  wizard.criteriaBlock ?? NO_CRITERIA_BLOCK,
                )}
                configFieldErrors={questionConfigFieldErrors(
                  wizard.configBlock ?? NO_CONFIG_BLOCK,
                )}
                emptyLabel={t('comingSoon')}
                dataTestId={`${TESTID}-step`}
              />

              {/* Divider separates the step content from the footer actions,
                  consistent across all three steps. */}
              <Divider />

              {/* Footer actions inside the card (matching the mock) — Skip on
                  the left, the step/publish trio on the right. All rendered
                  from one ActionButton so the four buttons don't duplicate
                  structure (DRY). */}
              <Row justifyContent="space-between" alignItems="center" flexWrap="wrap">
                <ActionButton action={skipAction} />
                <Row gridGap="dimensions.spacing.200" alignItems="center">
                  {stepActions.map((action) => (
                    <ActionButton key={action.testId} action={action} />
                  ))}
                </Row>
              </Row>
            </Col>
          </Card>
        </Col>

        <Col flex={1} minWidth={0} gridGap="dimensions.spacing.200">
          <AstraQuestionsPanel
            questions={wizard.questions}
            selectedId={wizard.selectedId}
            onSelect={wizard.selectQuestion}
            onDelete={wizard.deleteQuestion}
            {...(programId !== undefined ? { programId } : {})}
            dataTestId={`${TESTID}-panel`}
          />
        </Col>
      </Row>
    </Col>
  );
}
